import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import groupService from '../../api/groupService';
import { useAuth } from '../../context/AuthContext';
import { useGroup } from '../../context/GroupContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faShareAlt, 
  faUserPlus, 
  faDownload, 
  faUsers, 
  faCopy, 
  faTimes,
  faCheckCircle,
  faXmark,
  faSearch,
  faChevronDown
} from '@fortawesome/free-solid-svg-icons';
import { copyToClipboardEnhanced } from '../../utils/linkUtils';
import AdminLayout from '../../components/AdminLayout';
import config from '../../config';
import * as XLSX from 'xlsx';
import '../../styles/FinancialsPage.css';

const MembersAdminPage = () => {
  const { id: groupId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { adminGroup } = useGroup();
  
  const [group, setGroup] = useState(null);
  const [members, setMembers] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [error, setError] = useState('');
  const [showShareModal, setShowShareModal] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFilter, setSelectedFilter] = useState('all'); // 'all', 'members', 'requests'
  const modalRef = useRef(null);
  const shareUrlRef = useRef(null);
  
  // Pledge success modal state
  const [showPledgeSuccessModal, setShowPledgeSuccessModal] = useState(false);
  const [pledgeAmount, setPledgeAmount] = useState('0');

  // Check for pledge success on mount
  useEffect(() => {
    const showSuccess = sessionStorage.getItem('showPledgeSuccessModal');
    const amount = sessionStorage.getItem('pledgeAmount');
    
    if (showSuccess === 'true') {
      setPledgeAmount(amount || '0');
      setShowPledgeSuccessModal(true);
      sessionStorage.removeItem('showPledgeSuccessModal');
      sessionStorage.removeItem('pledgeAmount');
    }
  }, []);

  // Fetch data
  useEffect(() => {
    const fetchData = async () => {
      if (!adminGroup?.id) return;
      
      setLoading(true);
      setError('');
      
      try {
        setGroup(adminGroup);

        // Fetch members
        const membersRes = await groupService.getGroupMembersWithDetails(adminGroup.id);
        setMembers(membersRes.data || []);

        // Fetch requests
        const requestsRes = await groupService.getGroupJoinRequests(adminGroup.id);
        setRequests(requestsRes.data || []);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load members data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [adminGroup]);

  // Handle approve request
  const handleApprove = async (requestId) => {
    try {
      await groupService.approveJoinRequest(group.id, requestId);
      setRequests(requests.filter(r => r.id !== requestId));
      // Refresh members
      const membersRes = await groupService.getGroupMembersWithDetails(group.id);
      setMembers(membersRes.data || []);
    } catch (err) {
      console.error('Error approving request:', err);
      alert('Failed to approve request');
    }
  };

  // Handle deny request
  const handleDeny = async (requestId) => {
    try {
      await groupService.denyJoinRequest(group.id, requestId);
      setRequests(requests.filter(r => r.id !== requestId));
    } catch (err) {
      console.error('Error denying request:', err);
      alert('Failed to deny request');
    }
  };

  // Handle bulk approve
  const handleBulkApprove = async () => {
    if (!window.confirm(`Approve all ${requests.length} requests?`)) return;
    
    setBulkLoading(true);
    try {
      await Promise.all(requests.map(r => groupService.approveJoinRequest(group.id, r.id)));
      setRequests([]);
      // Refresh members
      const membersRes = await groupService.getGroupMembersWithDetails(group.id);
      setMembers(membersRes.data || []);
    } catch (err) {
      console.error('Error bulk approving:', err);
      alert('Failed to approve all requests');
    } finally {
      setBulkLoading(false);
    }
  };

  // Handle share
  const handleShare = () => {
    setShowShareModal(true);
  };

  const handleCloseShareModal = () => {
    setShowShareModal(false);
    setCopySuccess(false);
  };

  const generateGroupUrl = () => {
    const baseUrl = config.clientUrl || window.location.origin;
    return `${baseUrl}/#/group/${group?.id}`;
  };

  const handleCopyToClipboard = async () => {
    try {
      const url = generateGroupUrl();
      await copyToClipboardEnhanced(url, shareUrlRef);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // Handle export to Excel
  const handleExportToExcel = () => {
    const data = members.map(member => ({
      Name: member.user?.name || 'Unknown',
      Email: member.user?.email || 'N/A',
      Status: 'Active Member',
      'Joined Date': new Date(member.created_at).toLocaleDateString()
    }));

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Members');
    XLSX.writeFile(wb, `${group?.name || 'Group'}_Members.xlsx`);
  };

  // Filter data
  const filteredData = (() => {
    let data = [];
    
    if (selectedFilter === 'all') {
      data = [
        ...requests.map(r => ({ ...r, type: 'request' })),
        ...members.map(m => ({ ...m, type: 'member' }))
      ];
    } else if (selectedFilter === 'requests') {
      data = requests.map(r => ({ ...r, type: 'request' }));
    } else {
      data = members.map(m => ({ ...m, type: 'member' }));
    }

    if (searchQuery) {
      data = data.filter(item => {
        const user = item.user;
        const name = user?.name?.toLowerCase() || '';
        const email = user?.email?.toLowerCase() || '';
        const query = searchQuery.toLowerCase();
        return name.includes(query) || email.includes(query);
      });
    }

    return data;
  })();

  // Click outside handler for modals
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        handleCloseShareModal();
      }
    };

    if (showShareModal) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showShareModal]);

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Handle member click
  const handleMemberClick = (userId) => {
    console.log('🔵 Member clicked:', { userId, groupId: adminGroup?.id || group?.id });
    const targetGroupId = adminGroup?.id || group?.id || groupId;
    console.log('🔵 Navigating to:', `/admin/dashboard/${targetGroupId}/members/${userId}`);
    navigate(`/admin/dashboard/${targetGroupId}/members/${userId}`);
  };

  // Render filters
  const renderFilters = () => (
    <div className="chip-filters-container">
      <div className="chip-filters">
        <button
          className={`chip-filter ${selectedFilter === 'all' ? 'active' : ''}`}
          onClick={() => setSelectedFilter('all')}
        >
          All ({members.length + requests.length})
        </button>
        <button
          className={`chip-filter ${selectedFilter === 'members' ? 'active' : ''}`}
          onClick={() => setSelectedFilter('members')}
        >
          Active Members ({members.length})
        </button>
        <button
          className={`chip-filter ${selectedFilter === 'requests' ? 'active' : ''}`}
          onClick={() => setSelectedFilter('requests')}
        >
          Pending Requests ({requests.length})
        </button>
      </div>
      
      {/* Right side controls */}
      <div className="right-controls">
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <FontAwesomeIcon icon={faSearch} className="text-gray-400 text-sm" />
          </div>
          <input
            type="text"
            placeholder="Search members..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 pr-4 py-2 w-64 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        
        <button
          onClick={handleExportToExcel}
          disabled={members.length === 0}
          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <FontAwesomeIcon icon={faDownload} /> Export
        </button>
        
        <button
          onClick={handleShare}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
        >
          <FontAwesomeIcon icon={faShareAlt} /> Share
        </button>
      </div>
    </div>
  );

  // Render table
  const renderTable = () => {
    if (loading) {
      return (
        <div className="flex justify-center items-center py-12">
          <div className="text-gray-500">Loading members...</div>
        </div>
      );
    }

    if (filteredData.length === 0) {
      return (
        <div className="empty-state">
          <div className="empty-state-icon">
            <FontAwesomeIcon icon={faUsers} />
          </div>
          <h3>No {selectedFilter === 'requests' ? 'pending requests' : selectedFilter === 'members' ? 'members' : 'users'} yet</h3>
          <p>
            {selectedFilter === 'requests' 
              ? 'Join requests will appear here' 
              : searchQuery 
              ? 'Try adjusting your search query' 
              : 'Share the Yesod app with your members to get started'}
          </p>
        </div>
      );
    }

    return (
      <div className="table-wrapper">
        <table className="pledge-table">
          <thead>
            <tr>
              <th className="col-member">Member</th>
              <th className="col-type">Email</th>
              <th className="col-status">Status</th>
              <th className="col-date">Joined / Requested</th>
              <th className="col-actions">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredData.map((item) => {
              const userId = item.user_id || item.user?.id || item.id;
              const isMember = item.type === 'member';
              
              return (
                <tr 
                  key={item.id || item.user_id} 
                  className={isMember ? 'cursor-pointer hover:bg-blue-50 transition-colors' : ''}
                  onClick={(e) => {
                    console.log('🟢 TR CLICKED:', { 
                      isMember, 
                      userId, 
                      item,
                      target: e.target,
                      currentTarget: e.currentTarget 
                    });
                    if (isMember && userId) {
                      handleMemberClick(userId);
                    }
                  }}
                  style={{ 
                    cursor: isMember ? 'pointer' : 'default',
                    userSelect: 'none'
                  }}
                >
                <td className="col-member">
                  <div className="flex items-center space-x-3">
                    <div className="flex-shrink-0">
                      {item.user?.avatar_url ? (
                        <img 
                          src={item.user.avatar_url} 
                          alt={item.user?.name}
                          className="w-10 h-10 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-semibold">
                          {item.user?.name?.charAt(0)?.toUpperCase() || '?'}
                        </div>
                      )}
                    </div>
                    <div>
                      <div className="font-medium text-gray-900">
                        {item.user?.name || 'Unknown User'}
                      </div>
                    </div>
                  </div>
                </td>
                
                <td className="col-type">
                  <div className="text-gray-700">
                    {item.user?.email || 'N/A'}
                  </div>
                </td>
                
                <td className="col-status">
                  {item.type === 'request' ? (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                      <FontAwesomeIcon icon={faUserPlus} className="mr-1" />
                      Pending Request
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      <FontAwesomeIcon icon={faCheckCircle} className="mr-1" />
                      Active Member
                    </span>
                  )}
                </td>
                
                <td className="col-date">
                  <div className="text-gray-700">
                    {formatDate(item.created_at)}
                  </div>
                </td>
                
                <td className="col-actions">
                  {item.type === 'request' ? (
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleApprove(item.id);
                        }}
                        className="px-3 py-1 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium"
                      >
                        Accept
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeny(item.id);
                        }}
                        className="px-3 py-1 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium"
                      >
                        Deny
                      </button>
                    </div>
                  ) : (
                    <div className="text-blue-600 text-sm font-medium">View Profile →</div>
                  )}
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <AdminLayout>
      <div className="financials-page">
        <div className="financials-container">
          {/* Page Header */}
          <div className="page-header">
            <div className="page-header-top">
              <h1>Members</h1>
              {requests.length > 1 && (
                <button 
                  className="add-donation-type-button"
                  onClick={handleBulkApprove}
                  disabled={bulkLoading}
                >
                  <FontAwesomeIcon icon={faCheckCircle} /> 
                  {bulkLoading ? 'Processing...' : `Accept All (${requests.length})`}
                </button>
              )}
            </div>
          </div>

          {/* Error State */}
          {error && (
            <div className="error-state" style={{ 
              background: '#fef2f2', 
              border: '1px solid #fecaca', 
              borderRadius: '8px', 
              padding: '16px', 
              marginBottom: '24px',
              color: '#dc2626'
            }}>
              <strong>Error:</strong> {error}
            </div>
          )}

          {/* Summary Box */}
          <div className="summary-box" style={{ marginBottom: '24px' }}>
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold text-gray-900">
                  {members.length + requests.length}
                </div>
                <div className="text-sm text-gray-600 mt-1">
                  Total Users ({members.length} active, {requests.length} pending)
                </div>
              </div>
              <div className="text-4xl text-blue-500">
                <FontAwesomeIcon icon={faUsers} />
              </div>
            </div>
          </div>

          {/* Filters */}
          {renderFilters()}

          {/* Table */}
          {renderTable()}
        </div>

        {/* Share Modal */}
        {showShareModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4" ref={modalRef}>
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-semibold text-gray-900">
                    Share Community
                  </h2>
                  <button
                    onClick={handleCloseShareModal}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <FontAwesomeIcon icon={faTimes} />
                  </button>
                </div>

                <p className="text-gray-600 mb-4">
                  Share the link below to invite others to join the community:
                </p>

                <div className="flex items-center space-x-2 mb-4">
                  <input
                    type="text"
                    readOnly
                    value={generateGroupUrl()}
                    ref={shareUrlRef}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm bg-gray-50"
                  />
                  <button
                    onClick={handleCopyToClipboard}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium whitespace-nowrap"
                  >
                    <FontAwesomeIcon icon={faCopy} className="mr-2" />
                    {copySuccess ? 'Copied!' : 'Copy'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Pledge Success Modal */}
        {showPledgeSuccessModal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999
          }}>
            <div style={{
              background: '#fff',
              borderRadius: '16px',
              padding: '40px',
              maxWidth: '500px',
              width: '90%',
              textAlign: 'center',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
            }}>
              <div style={{
                width: '80px',
                height: '80px',
                background: '#10b981',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 24px',
                fontSize: '40px'
              }}>
                ✅
              </div>
              
              <h2 style={{
                fontSize: '28px',
                fontWeight: 'bold',
                color: '#1f2937',
                marginBottom: '12px'
              }}>
                Pledge Accepted!
              </h2>
              
              <p style={{
                fontSize: '18px',
                color: '#6b7280',
                marginBottom: '24px',
                lineHeight: '1.6'
              }}>
                Your community has been set up and the pledge of <strong>${parseFloat(pledgeAmount).toFixed(2)}</strong> has been added to your account.
              </p>
              
              <p style={{
                fontSize: '16px',
                color: '#6b7280',
                marginBottom: '32px'
              }}>
                The donor will now be able to complete their payment.
              </p>
              
              <button
                onClick={() => setShowPledgeSuccessModal(false)}
                style={{
                  width: '100%',
                  padding: '14px',
                  fontSize: '16px',
                  fontWeight: '600',
                  color: '#fff',
                  background: '#8b5cf6',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  transition: 'background 0.2s'
                }}
                onMouseEnter={(e) => e.target.style.background = '#7c3aed'}
                onMouseLeave={(e) => e.target.style.background = '#8b5cf6'}
              >
                Get Started
              </button>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default MembersAdminPage;
